import { useEffect, useState } from "react";
import { apiTech } from "@/lib/api";
import { EmptyState, StatusPill } from "@/components/admin";
import { ChevronRight } from "lucide-react";

export default function TechHistory({ onOpenJob }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiTech.get("/tech/jobs/history?limit=80")
      .then((r) => setJobs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const groups = groupByDayDesc(jobs);

  return (
    <>
      <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium pt-1 pb-1">
        Completed <span className="text-[#FFFFFF]/45 num">· {jobs.length}</span>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-24 bg-[#002060] border border-[#FFFFFF]/15 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg">
          <EmptyState message="No completed jobs yet." />
        </div>
      ) : (
        groups.map(([day, items, dayTotal]) => (
          <section key={day} className="mt-3 first:mt-0">
            <header className="flex items-center justify-between py-1.5">
              <div className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold">
                {day} <span className="text-[#FFFFFF]/45 num">· {items.length}</span>
              </div>
              <div className="text-xs text-[#FFFFFF] font-semibold num">${dayTotal.toFixed(0)}</div>
            </header>
            <ul className="space-y-2">
              {items.map((j) => (
                <li key={j.id}>
                  <button
                    type="button"
                    onClick={() => onOpenJob(j.id)}
                    data-testid={`tech-hist-${j.id}`}
                    className="w-full text-left bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-3 hover:border-[#FF6600] active:scale-[0.99] transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize text-sm">{j.appliance}</span>
                          <StatusPill status={j.status} />
                        </div>
                        <div className="text-xs text-[#FFFFFF]/70 mt-0.5 line-clamp-1">{j.customer_name}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {j.final_price != null && (
                          <span className="text-[#FFFFFF] font-semibold num text-sm">
                            ${Number(j.final_price).toFixed(0)}
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-[#FFFFFF]/45" />
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </>
  );
}

function groupByDayDesc(jobs) {
  const fmt = new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" });
  const buckets = new Map();
  for (const j of jobs) {
    const d = j.updated_at ? new Date(j.updated_at) : null;
    if (!d || isNaN(d.valueOf())) continue;
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!buckets.has(dayKey)) {
      buckets.set(dayKey, { label: fmt.format(d), items: [], total: 0, ts: d.valueOf() });
    }
    const b = buckets.get(dayKey);
    b.items.push(j);
    b.total += Number(j.final_price) || 0;
  }
  return Array.from(buckets.values())
    .sort((a, b) => b.ts - a.ts)
    .map((b) => [b.label, b.items, b.total]);
}
